import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useApolloClient } from '@apollo/client';
import {
	Form,
	Input,
	InputNumber,
	Select,
	Switch,
	Button,
	Card,
	Divider,
	Space,
	Collapse,
	message,
	Row,
	Col,
	Typography,
	Popconfirm,
	Alert,
	Spin,
} from 'antd';
import {
	PlusOutlined,
	DeleteOutlined,
	SaveOutlined,
	ExportOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import { configMode } from 'src/_shared/api/';
import FundingUnavailable from 'src/_shared/components/funding-unavailable/FundingUnavailableComponent.jsx';
import { lambda } from 'src/_shared/services/utils.js';
import uuid from 'uuid/v4';
import {
	ACTION_TYPES,
	POINT_CATEGORIES,
	CRITERIA_GROUPS,
	PRIMARY_PERIODS,
	ACTION_LABELS,
	ROLE_OPTIONS,
	QueryCompanyEntities,
	GetPointsSettings,
	UpdatePointsSettings,
} from './pointsSettingsHelpers';
import PointsLog from './PointsLogComponent';

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

const NewPointsSettings = () => {
	const [form] = Form.useForm();
	const [hasChanges, setHasChanges] = useState(false);
	const [loading, setLoading] = useState(false);
	const [initialLoading, setInitialLoading] = useState(true);
	const [departments, setDepartments] = useState([]);
	const [subCompanies, setSubCompanies] = useState([]);
	const [userGroups, setUserGroups] = useState([]);
	const [actions, setActions] = useState([]);
	const [customActions, setCustomActions] = useState([]);

	const client = useApolloClient();
	const currentUser = useSelector((state) => state?.user?.currentUser);
	const departmentLabel = currentUser?.company?.labelDepartment || 'Department';
	const subCompanyLabel =
		currentUser?.company?.subCompanyLabel || 'Sub Company';
	const customPointsActions = currentUser?.featureFlags?.includes(
		'customPointsActions'
	);
	const pointsEditGroupPriority = currentUser?.featureFlags?.includes(
		'pointsEditGroupPriority'
	);
	const recognitionEnabled =
		currentUser?.company?.enableRecognition ||
		currentUser?.featureFlags?.includes('recognitionPlatform');

	const globalRedeemable = Form.useWatch('redeemable', form);

	const fetchCompanyEntities = useCallback(async () => {
		if (!currentUser?.companyId) return;

		try {
			const { data } = await client.query({
				query: QueryCompanyEntities,
				variables: { companyId: currentUser.companyId },
				fetchPolicy: 'network-only',
			});

			const entities = data?.queryCompanyEntities || {};
			setDepartments(
				[...(entities.departments || [])].sort((a, b) =>
					a.name.localeCompare(b.name)
				)
			);
			setSubCompanies(
				[...(entities.subCompanies || [])].sort((a, b) =>
					a.name.localeCompare(b.name)
				)
			);
			setUserGroups(
				[...(entities.userGroups || [])].sort((a, b) =>
					a.name.localeCompare(b.name)
				)
			);
		} catch (error) {
			console.error('Error fetching company entities:', error);
			message.error('Failed to load company entities');
		}
	}, [client, currentUser?.companyId]);

	const fetchPointsSettings = useCallback(async () => {
		if (!currentUser?.companyId) return;

		try {
			const { data } = await client.query({
				query: GetPointsSettings,
				variables: { companyId: currentUser.companyId },
				fetchPolicy: 'network-only',
			});

			const settings = data?.getPointsSettings;

			if (settings) {
				form.setFieldsValue({
					redeemable: settings.redeemable ?? true,
					primaryPeriod: settings.primaryPeriod ?? 'ALLTIME',
					maxWeeklyShares: settings.maxWeeklyShares ?? 5,
					pointsRatio: settings.pointsRatio ?? null,
					maxRedeemable30: settings.maxRedeemable30 ?? null,
					maxRedeemable365: settings.maxRedeemable365 ?? null,
					groupPriority: settings.groupPriority ?? [
						'ROLE',
						'SUB_COMPANY',
						'DEPARTMENT',
						'USER_GROUP',
					],
					excludeRecognitionPointsFromReset:
						settings.excludeRecognitionPointsFromReset ?? false,
					excludeSpendablePointsFromReset:
						settings.excludeSpendablePointsFromReset ?? false,
					exclusions: settings.exclusions ?? {
						departmentIds: [],
						roles: [],
						subCompanyIds: [],
						userGroupIds: [],
					},
				});

				if (settings.actions && settings.actions.length > 0) {
					setActions(settings.actions);
				} else {
					initializeDefaultActions();
				}

				if (settings.customActions && settings.customActions.length > 0) {
					setCustomActions(settings.customActions);
				}
			} else {
				// No settings found, use defaults
				form.setFieldsValue({
					redeemable: true,
					primaryPeriod: 'ALLTIME',
					maxWeeklyShares: 5,
					pointsRatio: null,
					maxRedeemable30: null,
					maxRedeemable365: null,
					groupPriority: ['ROLE', 'SUB_COMPANY', 'DEPARTMENT', 'USER_GROUP'],
					excludeRecognitionPointsFromReset: false,
					excludeSpendablePointsFromReset: false,
					exclusions: {
						departmentIds: [],
						roles: [],
						subCompanyIds: [],
						userGroupIds: [],
					},
				});
				initializeDefaultActions();
			}
		} catch (error) {
			console.error('Error fetching points settings:', error);
			// On error, use defaults
			form.setFieldsValue({
				redeemable: true,
				primaryPeriod: 'ALLTIME',
				maxWeeklyShares: 5,
				pointsRatio: null,
				maxRedeemable30: null,
				maxRedeemable365: null,
				groupPriority: ['ROLE', 'SUB_COMPANY', 'DEPARTMENT', 'USER_GROUP'],
				excludeRecognitionPointsFromReset: false,
				excludeSpendablePointsFromReset: false,
				exclusions: {
					departmentIds: [],
					roles: [],
					subCompanyIds: [],
					userGroupIds: [],
				},
			});
			initializeDefaultActions();
		} finally {
			setInitialLoading(false);
		}
	}, [client, currentUser?.companyId, form]);

	const initializeDefaultActions = useCallback(() => {
		const defaultActions = Object.entries(ACTION_TYPES)
			.filter(([key]) => key !== 'CUSTOM_ACTION')
			.map(([key, value]) => ({
				actionId: value,
				actionType: value,
				label: ACTION_LABELS[key] || key.replace(/_/g, ' '),
				amount: 0,
				redeemable: false,
				category: key.includes('REFERRAL')
					? POINT_CATEGORIES.REFERRAL
					: key.includes('RECOGNITION')
						? POINT_CATEGORIES.RECOGNITION
						: POINT_CATEGORIES.ENGAGEMENT,
				customRules: [],
			}));
		setActions(defaultActions);
	}, []);

	useEffect(() => {
		const loadData = async () => {
			setInitialLoading(true);
			await fetchCompanyEntities();
			await fetchPointsSettings();
		};
		loadData();
	}, [fetchCompanyEntities, fetchPointsSettings]);

	const addCustomRule = (actionId) => {
		setActions((prevActions) =>
			prevActions.map((action) => {
				if (action.actionId === actionId) {
					return {
						...action,
						customRules: [
							...action.customRules,
							{
								customActionRuleId: uuid(),
								label: '',
								description: '',
								amount: 0,
								redeemable: false,
								criteria: {
									departmentIds: [],
									roles: [],
									subCompanyIds: [],
									userGroupIds: [],
								},
							},
						],
					};
				}
				return action;
			})
		);
		setHasChanges(true);
	};

	const removeCustomRule = (actionId, ruleId) => {
		setActions((prevActions) =>
			prevActions.map((action) => {
				if (action.actionId === actionId) {
					return {
						...action,
						customRules: action.customRules.filter(
							(rule) => rule.customActionRuleId !== ruleId
						),
					};
				}
				return action;
			})
		);
		setHasChanges(true);
	};

	const updateActionField = (actionId, field, value) => {
		setActions((prevActions) =>
			prevActions.map((action) =>
				action.actionId === actionId ? { ...action, [field]: value } : action
			)
		);
		setHasChanges(true);
	};

	const updateCustomRuleField = (actionId, ruleId, field, value) => {
		setActions((prevActions) =>
			prevActions.map((action) => {
				if (action.actionId === actionId) {
					return {
						...action,
						customRules: action.customRules.map((rule) =>
							rule.customActionRuleId === ruleId
								? { ...rule, [field]: value }
								: rule
						),
					};
				}
				return action;
			})
		);
		setHasChanges(true);
	};

	const updateCustomRuleCriteria = (actionId, ruleId, criteriaField, value) => {
		setActions((prevActions) =>
			prevActions.map((action) => {
				if (action.actionId === actionId) {
					return {
						...action,
						customRules: action.customRules.map((rule) =>
							rule.customActionRuleId === ruleId
								? {
										...rule,
										criteria: {
											...rule.criteria,
											[criteriaField]: value,
										},
									}
								: rule
						),
					};
				}
				return action;
			})
		);
		setHasChanges(true);
	};

	const isCriteriaUnique = (actionId, currentRuleId, criteria) => {
		const action = actions.find((a) => a.actionId === actionId);
		if (!action) return true;

		const otherRules = action.customRules.filter(
			(r) => r.customActionRuleId !== currentRuleId
		);

		return !otherRules.some((rule) => {
			const criteriaMatch =
				JSON.stringify(
					[
						...[...rule.criteria.roles].sort(),
						...[...rule.criteria.departmentIds].sort(),
						...[...rule.criteria.subCompanyIds].sort(),
						...[...rule.criteria.userGroupIds].sort(),
					].sort()
				) ===
				JSON.stringify(
					[
						...[...criteria.roles].sort(),
						...[...criteria.departmentIds].sort(),
						...[...criteria.subCompanyIds].sort(),
						...[...criteria.userGroupIds].sort(),
					].sort()
				);
			return criteriaMatch;
		});
	};

	const addCustomAction = () => {
		const newCustomAction = {
			customActionId: uuid(),
			actionType: ACTION_TYPES.CUSTOM_ACTION,
			label: '',
			amount: 0,
			description: '',
			redeemable: false,
			category: POINT_CATEGORIES.CUSTOM,
			customRules: [],
		};
		setCustomActions([...customActions, newCustomAction]);
		setHasChanges(true);
	};

	const removeCustomAction = (customActionId) => {
		setCustomActions(
			customActions.filter((action) => action.customActionId !== customActionId)
		);
		setHasChanges(true);
	};

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();
			setLoading(true);

			// Validate custom rules don't have duplicate criteria
			for (const action of actions) {
				for (const rule of action.customRules) {
					if (
						!isCriteriaUnique(
							action.actionId,
							rule.customActionRuleId,
							rule.criteria
						)
					) {
						message.error(
							`Duplicate criteria found in custom rules for "${action.label}"`
						);
						setLoading(false);
						return;
					}
				}
			}

			const groupPriority =
				values.groupPriority && values.groupPriority.length > 0
					? values.groupPriority
					: ['ROLE', 'SUB_COMPANY', 'DEPARTMENT', 'USER_GROUP'];

			const settingsInput = {
				...values,
				groupPriority,
				actions,
				customActions,
			};

			await client.mutate({
				mutation: UpdatePointsSettings,
				variables: {
					companyId: currentUser.companyId,
					input: settingsInput,
				},
			});

			message.success('Points settings saved successfully');
			setHasChanges(false);
		} catch (error) {
			console.error('Error saving settings:', error);
			message.error('Failed to save settings');
		} finally {
			setLoading(false);
		}
	};

	const handleExportGiftCardData = () => {
		let endpoint = '';
		endpoint =
			configMode === 'DEV'
				? 'export-gifts-log-dev-sendEmail'
				: 'export-gifts-log-prod-sendEmail';
		const { companyId, cognitoId } = currentUser;
		message.success('Export processing. You will receive an email when ready');
		lambda({ endpoint, variables: { companyId, cognitoId } });
	};

	if (initialLoading) {
		return (
			<div
				style={{
					padding: '24px',
					maxWidth: '1400px',
					margin: '0 auto',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '400px',
				}}
			>
				<Spin size="large" tip="Loading points settings..." />
			</div>
		);
	}

	return (
		<div
			style={{
				maxWidth: '1400px',
				margin: '0 auto',
				position: 'relative',
			}}
		>
			<div>
				<Title level={2}>Points Settings</Title>
			</div>
			<div
				style={{
					position: 'sticky',
					top: '16px',
					zIndex: 100,
					display: 'flex',
					justifyContent: 'flex-end',
					marginTop: '-60px',
					marginBottom: '12px',
					pointerEvents: 'none',
				}}
			>
				<div style={{ pointerEvents: 'auto' }}>
					<Space>
						<Button
							type="primary"
							size="large"
							icon={<SaveOutlined />}
							onClick={handleSubmit}
							loading={loading}
							disabled={!hasChanges}
						>
							Save Settings
						</Button>
					</Space>
				</div>
			</div>
			{hasChanges && (
				<Alert
					message="You have unsaved changes"
					type="warning"
					showIcon
					style={{ marginBottom: '24px' }}
				/>
			)}
			<Form
				form={form}
				layout="vertical"
				onValuesChange={() => setHasChanges(true)}
			>
				<Card
					title={<span style={{ fontWeight: 600 }}>Global Settings</span>}
					style={{
						marginBottom: '24px',
						borderRadius: '12px',
						border: '1px solid #ddd',
					}}
				>
					<Row gutter={32}>
						<Col xs={24} lg={12}>
							<Row gutter={16}>
								<Col xs={24} md={12}>
									<Form.Item
										label="Points Redeemable"
										name="redeemable"
										valuePropName="checked"
										tooltip="Global toggle for if points are redeemable in the Rewards Store"
									>
										<Switch />
									</Form.Item>
								</Col>
								<Col xs={24} md={12}>
									<Form.Item
										label="Reset Period"
										name="primaryPeriod"
										tooltip="Totals for balances, leaderboards, and redeemable points will be reset to 0 for all users at the start of a new period. Note: Historic log data is not lost."
									>
										<Select>
											{Object.entries(PRIMARY_PERIODS).map(([key, label]) => (
												<Option key={key} value={key}>
													{label}
												</Option>
											))}
										</Select>
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={16}>
								<Col xs={24} md={12}>
									<Form.Item
										label="Max Weekly Referral Link Shares"
										name="maxWeeklyShares"
										tooltip="Maximum number of times per week a user can earn points for sharing a referral link"
									>
										<InputNumber min={0} style={{ width: '100%' }} />
									</Form.Item>
								</Col>
								<Col xs={24} md={12}>
									<Form.Item
										label="Exclude Redeemable Points From Reset Period"
										name="excludeSpendablePointsFromReset"
										valuePropName="checked"
										tooltip='Redeemable points will ignore the Reset Period setting and remain visible and spendable in the Rewards Store. If Reset Period is "Never Reset", this setting has no effect.'
									>
										<Switch />
									</Form.Item>
								</Col>
							</Row>
							<Form.Item
								name="excludeRecognitionPointsFromReset"
								hidden // intentional, used to persist existing value, but hide from view
								valuePropName="checked"
							>
								<Switch />
							</Form.Item>
							{pointsEditGroupPriority && (
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item
											label="Group Priority"
											name="groupPriority"
											tooltip="Priority order when a user matches multiple custom rules"
										>
											<Select mode="multiple">
												{CRITERIA_GROUPS.map((group) => (
													<Option key={group} value={group}>
														{group.replace(/_/g, ' ')}
													</Option>
												))}
											</Select>
										</Form.Item>
									</Col>
								</Row>
							)}
						</Col>
						<Col
							xs={0}
							lg={0}
							style={{
								display: 'none',
							}}
						>
							<Divider type="vertical" style={{ height: '100%' }} />
						</Col>
						<Col
							xs={24}
							lg={12}
							style={{ borderLeft: '1px solid #f0f0f0', paddingLeft: '24px' }}
						>
							<div style={{ marginBottom: '16px' }}>
								<Text strong style={{ fontSize: '14px' }}>
									Global Exclusions
								</Text>
								<Text
									type="secondary"
									style={{ display: 'block', marginTop: '4px' }}
								>
									Users matching these criteria will be excluded from earning
									points
								</Text>
							</div>
							<Row gutter={16}>
								<Col xs={24} md={12}>
									<Form.Item label="By Role" name={['exclusions', 'roles']}>
										<Select
											mode="multiple"
											placeholder="Select exclusions by Role"
										>
											{ROLE_OPTIONS.map((role) => (
												<Option key={role.value} value={role.value}>
													{role.label}
												</Option>
											))}
										</Select>
									</Form.Item>
								</Col>
								<Col xs={24} md={12}>
									<Form.Item
										label={`By ${departmentLabel}`}
										name={['exclusions', 'departmentIds']}
									>
										<Select
											mode="multiple"
											placeholder={`Select exclusions by ${departmentLabel}`}
											disabled={departments.length === 0}
										>
											{departments.map((dept) => (
												<Option key={dept.id} value={dept.id}>
													{dept.name}
												</Option>
											))}
										</Select>
									</Form.Item>
								</Col>
								<Col xs={24} md={12}>
									<Form.Item
										label={`By ${subCompanyLabel}`}
										name={['exclusions', 'subCompanyIds']}
									>
										<Select
											mode="multiple"
											placeholder={`Select exclusions by ${subCompanyLabel}`}
											disabled={subCompanies.length === 0}
										>
											{subCompanies.map((sub) => (
												<Option key={sub.id} value={sub.id}>
													{sub.name}
												</Option>
											))}
										</Select>
									</Form.Item>
								</Col>
								<Col xs={24} md={12}>
									<Form.Item
										label="By User Group"
										name={['exclusions', 'userGroupIds']}
									>
										<Select
											mode="multiple"
											placeholder="Select exclusions by User Group"
											disabled={userGroups.length === 0}
										>
											{userGroups.map((group) => (
												<Option key={group.id} value={group.id}>
													{group.name}
												</Option>
											))}
										</Select>
									</Form.Item>
								</Col>
							</Row>
						</Col>
					</Row>
				</Card>
				<Card
					title={<span style={{ fontWeight: 600 }}>Points Events</span>}
					style={{
						marginBottom: '24px',
						borderRadius: '12px',
						border: '1px solid #ddd',
					}}
					extra={
						<Text type="secondary">
							Set point event amounts, redeemability, and add custom rules for
							alternative values
						</Text>
					}
				>
					<Collapse>
						{actions
							.filter((action) => {
								// Hide recognition actions if recognition is not enabled
								if (!recognitionEnabled) {
									return (
										action.actionType !== ACTION_TYPES.RECOGNITION_SENT &&
										action.actionType !== ACTION_TYPES.RECOGNITION_RECEIVED
									);
								}
								return true;
							})
							.map((action) => (
								<Panel
									header={
										<div
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												width: '100%',
												paddingRight: '24px',
											}}
										>
											<Text strong>{action.label}</Text>
											<Space size="large" style={{ marginLeft: 'auto' }}>
												<Text type="secondary">
													Points Amount: <Text strong>{action.amount}</Text>
												</Text>
												<Text type="secondary">
													Redeemable:{' '}
													<Text strong>{action.redeemable ? 'Yes' : 'No'}</Text>
												</Text>
												<Text type="secondary">
													Custom Rules:{' '}
													<Text strong>{action.customRules.length}</Text>
												</Text>
											</Space>
										</div>
									}
									key={action.actionId}
								>
									<Row gutter={16} style={{ marginBottom: '16px' }}>
										<Col xs={24} md={8}>
											<Space direction="vertical" style={{ width: '100%' }}>
												<Text strong>Default Points Amount</Text>
												<InputNumber
													min={0}
													value={action.amount}
													onChange={(value) =>
														updateActionField(action.actionId, 'amount', value)
													}
													style={{ width: '100%' }}
												/>
											</Space>
										</Col>
										<Col xs={24} md={8}>
											<Space direction="vertical">
												<Text strong>Redeemable</Text>
												<Switch
													checked={action.redeemable}
													onChange={(checked) =>
														updateActionField(
															action.actionId,
															'redeemable',
															checked
														)
													}
													disabled={!globalRedeemable}
												/>
											</Space>
										</Col>
									</Row>
									<Divider orientation="left">Custom Rules</Divider>
									{action.customRules.map((rule, index) => {
										const otherRules = action.customRules.filter(
											(r) => r.customActionRuleId !== rule.customActionRuleId
										);
										const usedRoles = new Set(
											otherRules.flatMap((r) => r.criteria.roles)
										);
										const usedDeptIds = new Set(
											otherRules.flatMap((r) => r.criteria.departmentIds)
										);
										const usedSubCoIds = new Set(
											otherRules.flatMap((r) => r.criteria.subCompanyIds)
										);
										const usedUgIds = new Set(
											otherRules.flatMap((r) => r.criteria.userGroupIds)
										);
										const hasDuplicatePointsRedeemable =
											(action.amount === rule.amount &&
												action.redeemable === rule.redeemable) ||
											otherRules.some(
												(r) =>
													r.amount === rule.amount &&
													r.redeemable === rule.redeemable
											);

										return (
											<Card
												key={rule.customActionRuleId}
												size="small"
												style={{ marginBottom: '16px' }}
												title={`Rule ${index + 1}`}
												extra={
													<Popconfirm
														title="Delete this custom rule?"
														onConfirm={() =>
															removeCustomRule(
																action.actionId,
																rule.customActionRuleId
															)
														}
													>
														<Button
															type="text"
															danger
															size="small"
															icon={<DeleteOutlined />}
														/>
													</Popconfirm>
												}
											>
												<Row gutter={16}>
													<Col xs={24} md={8}>
														<Space
															direction="vertical"
															style={{ width: '100%' }}
														>
															<Text>Label</Text>
															<Input
																value={rule.label}
																onChange={(e) =>
																	updateCustomRuleField(
																		action.actionId,
																		rule.customActionRuleId,
																		'label',
																		e.target.value
																	)
																}
																placeholder="e.g., Employees with Non-Redeemable Points"
															/>
														</Space>
													</Col>
													<Col xs={24} md={8}>
														<Space
															direction="vertical"
															style={{ width: '100%' }}
														>
															<div>
																<Text>Points Amount</Text>
																{hasDuplicatePointsRedeemable && (
																	<Text
																		type="warning"
																		style={{
																			fontSize: '12px',
																			display: 'block',
																		}}
																	>
																		<WarningOutlined
																			style={{ marginRight: 4 }}
																		/>
																		There already exists a rule with the same
																		Points and Redeemable values for this action
																	</Text>
																)}
															</div>
															<InputNumber
																min={0}
																value={rule.amount}
																onChange={(value) =>
																	updateCustomRuleField(
																		action.actionId,
																		rule.customActionRuleId,
																		'amount',
																		value
																	)
																}
																style={{ width: '100%' }}
															/>
														</Space>
													</Col>
													<Col xs={24} md={8}>
														<Space direction="vertical">
															<Text>Redeemable</Text>
															<Switch
																checked={rule.redeemable}
																onChange={(checked) =>
																	updateCustomRuleField(
																		action.actionId,
																		rule.customActionRuleId,
																		'redeemable',
																		checked
																	)
																}
																disabled={!globalRedeemable}
															/>
														</Space>
													</Col>
												</Row>

												<Divider style={{ margin: '12px 0' }} />

												<Row gutter={16}>
													<Col xs={24} md={12} style={{ marginBottom: 6 }}>
														<Space
															direction="vertical"
															style={{ width: '100%' }}
														>
															<Text>Role Selections</Text>
															<Select
																mode="multiple"
																value={rule.criteria.roles}
																onChange={(value) =>
																	updateCustomRuleCriteria(
																		action.actionId,
																		rule.customActionRuleId,
																		'roles',
																		value
																	)
																}
																style={{ width: '100%' }}
																placeholder="Select by Role"
															>
																{ROLE_OPTIONS.filter(
																	(role) =>
																		!usedRoles.has(role.value) ||
																		rule.criteria.roles.includes(role.value)
																).map((role) => (
																	<Option key={role.value} value={role.value}>
																		{role.label}
																	</Option>
																))}
															</Select>
														</Space>
													</Col>
													<Col xs={24} md={12} style={{ marginBottom: 6 }}>
														<Space
															direction="vertical"
															style={{ width: '100%' }}
														>
															<Text>{`${departmentLabel} Selections`}</Text>
															<Select
																mode="multiple"
																value={rule.criteria.departmentIds}
																onChange={(value) =>
																	updateCustomRuleCriteria(
																		action.actionId,
																		rule.customActionRuleId,
																		'departmentIds',
																		value
																	)
																}
																style={{ width: '100%' }}
																placeholder={`Select by ${departmentLabel}`}
															>
																{departments
																	.filter(
																		(dept) =>
																			!usedDeptIds.has(dept.id) ||
																			rule.criteria.departmentIds.includes(
																				dept.id
																			)
																	)
																	.map((dept) => (
																		<Option key={dept.id} value={dept.id}>
																			{dept.name}
																		</Option>
																	))}
															</Select>
														</Space>
													</Col>
													<Col xs={24} md={12}>
														<Space
															direction="vertical"
															style={{ width: '100%' }}
														>
															<Text>{`${subCompanyLabel} Selections`}</Text>
															<Select
																mode="multiple"
																value={rule.criteria.subCompanyIds}
																onChange={(value) =>
																	updateCustomRuleCriteria(
																		action.actionId,
																		rule.customActionRuleId,
																		'subCompanyIds',
																		value
																	)
																}
																style={{ width: '100%' }}
																placeholder={`Select by ${subCompanyLabel}`}
															>
																{subCompanies
																	.filter(
																		(sub) =>
																			!usedSubCoIds.has(sub.id) ||
																			rule.criteria.subCompanyIds.includes(
																				sub.id
																			)
																	)
																	.map((sub) => (
																		<Option key={sub.id} value={sub.id}>
																			{sub.name}
																		</Option>
																	))}
															</Select>
														</Space>
													</Col>
													<Col xs={24} md={12}>
														<Space
															direction="vertical"
															style={{ width: '100%' }}
														>
															<Text>User Group Selections</Text>
															<Select
																mode="multiple"
																value={rule.criteria.userGroupIds}
																onChange={(value) =>
																	updateCustomRuleCriteria(
																		action.actionId,
																		rule.customActionRuleId,
																		'userGroupIds',
																		value
																	)
																}
																style={{ width: '100%' }}
																placeholder="Select by User Group"
															>
																{userGroups
																	.filter(
																		(group) =>
																			!usedUgIds.has(group.id) ||
																			rule.criteria.userGroupIds.includes(
																				group.id
																			)
																	)
																	.map((group) => (
																		<Option key={group.id} value={group.id}>
																			{group.name}
																		</Option>
																	))}
															</Select>
														</Space>
													</Col>
												</Row>

												<div style={{ marginTop: '12px' }}>
													<Input.TextArea
														value={rule.description}
														onChange={(e) =>
															updateCustomRuleField(
																action.actionId,
																rule.customActionRuleId,
																'description',
																e.target.value
															)
														}
														placeholder="Optional description"
														rows={2}
													/>
												</div>
											</Card>
										);
									})}
									<Button
										type="dashed"
										onClick={() => addCustomRule(action.actionId)}
										icon={<PlusOutlined />}
										block
									>
										Add Custom Rule
									</Button>
								</Panel>
							))}
					</Collapse>
				</Card>
				{customPointsActions && (
					<Card
						title="Custom Actions"
						style={{
							marginBottom: '24px',
							borderRadius: '12px',
							border: '1px solid #ddd',
						}}
					>
						<Text
							type="secondary"
							style={{ display: 'block', marginBottom: '16px' }}
						>
							Create your own custom point-earning actions
						</Text>

						{customActions.length === 0 ? (
							<Alert
								message="No custom actions yet"
								description="Click the button below to create a custom action"
								type="info"
								showIcon
								style={{ marginBottom: '16px' }}
							/>
						) : (
							<Space direction="vertical" style={{ width: '100%' }}>
								{customActions.map((customAction) => (
									<Card
										key={customAction.customActionId}
										size="small"
										title="Custom Action"
										extra={
											<Popconfirm
												title="Delete this custom action?"
												onConfirm={() =>
													removeCustomAction(customAction.customActionId)
												}
											>
												<Button
													type="text"
													danger
													size="small"
													icon={<DeleteOutlined />}
												/>
											</Popconfirm>
										}
									>
										<Row gutter={16}>
											<Col xs={24} md={12}>
												<Space direction="vertical" style={{ width: '100%' }}>
													<Text>Action Label</Text>
													<Input
														value={customAction.label}
														onChange={(e) => {
															setCustomActions(
																customActions.map((a) =>
																	a.customActionId ===
																	customAction.customActionId
																		? { ...a, label: e.target.value }
																		: a
																)
															);
															setHasChanges(true);
														}}
														placeholder="e.g., Complete Training Module"
													/>
												</Space>
											</Col>
											<Col xs={24} md={6}>
												<Space direction="vertical" style={{ width: '100%' }}>
													<Text>Default Points</Text>
													<InputNumber
														min={0}
														value={customAction.amount}
														onChange={(value) => {
															setCustomActions(
																customActions.map((a) =>
																	a.customActionId ===
																	customAction.customActionId
																		? { ...a, amount: value }
																		: a
																)
															);
															setHasChanges(true);
														}}
														style={{ width: '100%' }}
													/>
												</Space>
											</Col>
											<Col xs={24} md={6}>
												<Space direction="vertical">
													<Text>Redeemable</Text>
													<Switch
														checked={customAction.redeemable}
														onChange={(checked) => {
															setCustomActions(
																customActions.map((a) =>
																	a.customActionId ===
																	customAction.customActionId
																		? { ...a, redeemable: checked }
																		: a
																)
															);
															setHasChanges(true);
														}}
														disabled={!globalRedeemable}
													/>
												</Space>
											</Col>
										</Row>
										<div style={{ marginTop: '12px' }}>
											<Text>Description</Text>
											<Input.TextArea
												value={customAction.description}
												onChange={(e) => {
													setCustomActions(
														customActions.map((a) =>
															a.customActionId === customAction.customActionId
																? { ...a, description: e.target.value }
																: a
														)
													);
													setHasChanges(true);
												}}
												placeholder="Optional description of this custom action"
												rows={2}
												style={{ marginTop: '8px' }}
											/>
										</div>
									</Card>
								))}
							</Space>
						)}
						<Button
							type="dashed"
							onClick={addCustomAction}
							icon={<PlusOutlined />}
							block
							style={{ marginTop: '16px' }}
						>
							Add Custom Action
						</Button>
					</Card>
				)}
				<Card
					title={<span style={{ fontWeight: 600 }}>Gift Card Store</span>}
					style={{
						marginBottom: '24px',
						borderRadius: '12px',
						border: '1px solid #ddd',
					}}
					extra={
						<Button
							icon={<ExportOutlined />}
							onClick={handleExportGiftCardData}
						>
							Export Gift Card Data
						</Button>
					}
				>
					<Row gutter={16}>
						<Col xs={24} md={12}>
							<div
								style={{
									marginBottom: '24px',
									display: 'flex',
									alignItems: 'center',
									gap: '16px',
								}}
							>
								<Text strong style={{ fontSize: '16px' }}>
									Company Balance:{' '}
									<span style={{ color: '#52c41a' }}>
										$
										{currentUser?.company?.giftCardStoreBalance
											? Number.parseFloat(
													currentUser.company.giftCardStoreBalance
												).toFixed(2)
											: '0.00'}
									</span>
								</Text>
								<FundingUnavailable style={{ maxWidth: 420 }} />
							</div>
							<div>
								<Text strong style={{ display: 'block', marginBottom: '8px' }}>
									Points-to-currency ratio:
								</Text>
								<Text
									type="secondary"
									style={{ display: 'block', marginBottom: '8px' }}
								>
									e.g. 100 points: 1.00 USD
								</Text>
								<Space>
									<Form.Item
										name="pointsRatio"
										style={{ marginBottom: 0 }}
										rules={[
											{
												required: false,
												message: 'Required for store functionality',
											},
										]}
									>
										<InputNumber
											min={1}
											placeholder="100"
											style={{ width: '120px' }}
										/>
									</Form.Item>
									<Text>: ${`1.00 ${currentUser?.currency || 'USD'}`}</Text>
								</Space>
							</div>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item
								label="Max Employee Gift Card Redemption - 30 Day Period"
								name="maxRedeemable30"
								tooltip="Maximum dollar amount an employee can redeem in gift cards within a 30-day period"
							>
								<InputNumber
									min={0}
									placeholder="e.g. 5"
									style={{ width: '100%' }}
									prefix="$"
								/>
							</Form.Item>
							<Form.Item
								label="Max Employee Gift Card Redemption - 365 Day Period"
								name="maxRedeemable365"
								tooltip="Maximum dollar amount an employee can redeem in gift cards within a 365-day period"
							>
								<InputNumber
									min={0}
									placeholder="e.g. 25"
									style={{ width: '100%' }}
									prefix="$"
								/>
							</Form.Item>
						</Col>
					</Row>
				</Card>
				<PointsLog />
			</Form>
		</div>
	);
};

export default NewPointsSettings;
